/**
 * Property AST readers for V2 editor
 *
 * These functions PARSE source code into data (parser construction, JSX element
 * lookup, read-only source/style inspection, lexical-scope checks). They never
 * mutate source code — see `@/ast/writers/properties/edit` for that.
 */

import { parse as babelParse, ParserPlugin } from '@babel/parser';
import { parse, types } from 'recast';

import type { FileContents, NodeLocation } from './types';

// Core AST primitives now live in ast/core; re-exported here for existing importers.
import { findJSXElementInAST } from '@/ast/core/babel-utils/babel-utils';
import { createParser } from '@/ast/core/create-parser/create-parser';
import { parserPlugins } from '@/ast/core/parser-plugins/parser-plugins';
import getFileLanguage from '@/shared/utils/get-file-language';

export type { FileContents, NodeLocation, TransformResult } from './types';
export { parserPlugins } from '@/ast/core/parser-plugins/parser-plugins';
export { createParser } from '@/ast/core/create-parser/create-parser';
export { findJSXElementInAST } from '@/ast/core/babel-utils/babel-utils';

/**
 * Extract the file key from the node's file path
 */
export function getFileKey(node: NodeLocation): string {
  const file = node.file;
  if (file.startsWith('/module:/')) {
    return file.slice('/module:/'.length);
  }
  return file;
}

/**
 * Read-only "Source" info for the inspector's Source section: which
 * `styles.<name>` StyleSheet ref backs the selected element's `style`, and how
 * many elements in the same file reference that same ref (so the user knows
 * whether an edit is direct or shared). Returns `variable: null` for
 * inline-only styles (no StyleSheet ref).
 */
export function getStyleSourceInfo(
  files: Record<string, { contents?: unknown } | null | undefined>,
  node: NodeLocation,
): { variable: string | null; usedBy: number } {
  const fileKey = getFileKey(node);
  const file = files[fileKey];
  const contents = file && (file as { contents?: unknown }).contents;
  if (typeof contents !== 'string') return { variable: null, usedBy: 0 };

  // Collect `styles.<name>` member refs from a JSX element's `style` attribute
  // (handles `style={styles.x}` and `style={[styles.x, {...}]}`).
  const refsOf = (jsxEl: any): string[] => {
    const attrs = jsxEl?.openingElement?.attributes || [];
    const styleAttr = attrs.find((a: any) => a.type === 'JSXAttribute' && a.name?.name === 'style');
    if (!styleAttr || styleAttr.value?.type !== 'JSXExpressionContainer') return [];
    const refs: string[] = [];
    const collect = (n: any) => {
      if (!n) return;
      if (
        n.type === 'MemberExpression' &&
        n.object?.type === 'Identifier' &&
        n.object.name === 'styles' &&
        n.property?.type === 'Identifier'
      ) {
        refs.push('styles.' + n.property.name);
      } else if (n.type === 'ArrayExpression') {
        (n.elements || []).forEach(collect);
      }
    };
    collect(styleAttr.value.expression);
    return refs;
  };

  try {
    const parser = createParser(fileKey);
    const ast = parse(contents, { parser });
    const line = parseInt(String(node.line), 10);
    const col = parseInt(String(node.col), 10);
    const targetPath = findJSXElementInAST(ast, line, col, node.type);
    if (!targetPath) return { variable: null, usedBy: 0 };

    const variable = refsOf(targetPath.node)[0] ?? null;
    if (!variable) return { variable: null, usedBy: 0 };

    let usedBy = 0;
    types.visit(ast, {
      visitJSXElement(p: any) {
        if (refsOf(p.node).includes(variable)) usedBy++;
        this.traverse(p);
      },
    });
    return { variable, usedBy };
  } catch {
    return { variable: null, usedBy: 0 };
  }
}

/** Does a `const { <accessor> } = ...` destructuring exist in this
 *  declaration? (e.g. `const { getThemeValue } = useTheme()`.) */
function declBindsAccessor(stmt: any, accessor: string): boolean {
  if (stmt?.type !== 'VariableDeclaration') return false;
  for (const d of stmt.declarations || []) {
    if (d.id?.type === 'ObjectPattern') {
      for (const p of d.id.properties || []) {
        const key = p.key;
        if (key && (key.name === accessor || key.value === accessor)) return true;
      }
    }
  }
  return false;
}

/** Are the function's params an object pattern binding `<accessor>`?
 *  (e.g. a component that takes `{ getThemeValue }` as a prop.) */
function paramsBindAccessor(params: any[], accessor: string): boolean {
  for (const param of params || []) {
    if (param?.type === 'ObjectPattern') {
      for (const p of param.properties || []) {
        const key = p.key;
        if (key && (key.name === accessor || key.value === accessor)) return true;
      }
    }
  }
  return false;
}

/**
 * True when `<accessor>` (default `getThemeValue`) is in lexical scope at the
 * JSX element identified by `node` — i.e. some enclosing function destructures
 * it (typically `const { getThemeValue } = useTheme()`) or receives it as a
 * param. Used to decide whether a theme *reference* can be written inline
 * safely, or whether we must fall back to a literal hex.
 */
export function isThemeAccessorInScope(
  files: FileContents,
  node: NodeLocation,
  accessor = 'getThemeValue',
): boolean {
  const fileKey = getFileKey(node);
  const file = files[fileKey];
  if (!file || file.type !== 'CODE' || typeof file.contents !== 'string') return false;
  // Cheap reject — no mention of the accessor anywhere means it can't be bound.
  if (!file.contents.includes(accessor)) return false;

  try {
    const ast = parse(file.contents, { parser: createParser(fileKey) });
    const targetLine = parseInt(String(node.line), 10);
    const targetCol = parseInt(String(node.col), 10);
    const jsxPath = findJSXElementInAST(ast, targetLine, targetCol, (node as any).type);
    if (!jsxPath) return false;

    // Walk every enclosing function; a binding in any of them is in scope.
    let p: any = jsxPath;
    while (p) {
      const n = p.node;
      if (
        n &&
        (n.type === 'FunctionDeclaration' ||
          n.type === 'FunctionExpression' ||
          n.type === 'ArrowFunctionExpression')
      ) {
        if (paramsBindAccessor(n.params, accessor)) return true;
        const body = n.body;
        if (body?.type === 'BlockStatement') {
          for (const stmt of body.body || []) {
            if (declBindsAccessor(stmt, accessor)) return true;
          }
        }
      }
      p = p.parentPath;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Unwrap a callback-style `style` prop to the underlying style node.
 * React Native commonly uses `style={({ pressed }) => [styles.x, ...]}` on
 * Pressable (and `style={() => (...)}` elsewhere). The actual style
 * array/object/StyleSheet-ref lives in the function's return value, so any
 * caller that reads or mutates styles must operate on that, not the function
 * node itself. Returns the original expression unchanged for non-callback styles.
 */
export function unwrapStyleExpression(expression: any): any {
  if (expression?.type === 'ArrowFunctionExpression' || expression?.type === 'FunctionExpression') {
    const body = expression.body;
    if (body?.type === 'BlockStatement') {
      const ret = (body.body || []).find((s: any) => s?.type === 'ReturnStatement');
      return ret?.argument ?? null;
    }
    // Expression-bodied arrow: `() => [...]` or `() => ({...})`
    return body ?? null;
  }
  return expression;
}
